import { React, useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../index.css"
import { Logo, LogoName1, s10 } from "../assets";
import { navLinks } from "../constants";
import { socialMedia } from "../constants";
import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { motion } from "framer-motion";
import { MdAdd, MdFormatListBulletedAdd, MdHdrPlus, MdOutlineStarBorder, MdStar } from "react-icons/md";
import styles from "../style";
import { FiArrowRight, FiCalendar } from "react-icons/fi";
import { Reveal } from "./reveal";

gsap.registerPlugin(SplitText);

const Navbar= () => {
  const [active, setActive] = useState("Home");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status on component mount
    const checkAuth = () => {
      const authStatus = localStorage.getItem('isAuthenticated') === 'true';
      setIsAuthenticated(authStatus);
    };
    
    checkAuth();
    
    // Listen for storage changes (in case auth changes in another tab)
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
    };
  }, []);

  const handleLogout = () => {
    // Clear user session
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('user');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('loginTime');
    
    setIsAuthenticated(false);
    
    // Redirect to home page
    navigate('/');
  };
  
  const navTogglerRef = useRef(null);
  const navBgRef = useRef([]);
  const navItemsRef = useRef(null);
  const navRef = useRef(null);
  const navContentRef = useRef(null);
  const tlRef = useRef(null);
  const splitLinksRef = useRef(null);
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Check if page is scrolled down (you can adjust the threshold)
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20); // Hide after scrolling 20px
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Clean up the event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Set active based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const activeLink = navLinks.find(link => link.href === currentPath);
    if (activeLink) {
      setActive(activeLink.title);
    } else if (currentPath === "/") {
      // Find the home link
      const homeLink = navLinks.find(link => link.href === "/");
      setActive(homeLink ? homeLink.title : "Home");
    }
  }, [location, navLinks]);

  useEffect(() => {
  if (isMenuOpen && navItemsRef.current) {
    // Clear any existing SplitText instances
    if (splitLinksRef.current) {
      splitLinksRef.current.revert();
    }
    
    const links = navItemsRef.current.querySelectorAll("a");
    console.log("Found links for SplitText:", links.length); // Debug log
    
    if (links.length) {
      splitLinksRef.current = SplitText.create(links, { 
        type: "lines",
        linesClass: "line",
      });
    }
  }
}, [isMenuOpen]);

  // Navbar scroll animation
  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        if (window.scrollY > 50) {
          navRef.current.classList.add("scrolled");
        } else {
          navRef.current.classList.remove("scrolled");
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    if (isMenuOpen) {
      handleCloseMenu();
    }
  }, [location.pathname]);

  // Initialize timeline
  useEffect(() => {
    if (navBgRef.current.length && navContentRef.current && navItemsRef.current) {
      // Set initial state
      gsap.set(navBgRef.current, { scaleY: 0 });
      gsap.set(navContentRef.current, { 
        display: "none",
        pointerEvents: "none" // Prevent interaction when hidden
      });
      
      tlRef.current = gsap.timeline({ 
        paused: true, 
        onComplete: () => { 
          setIsAnimating(false);
          // Enable pointer events when animation completes
          gsap.set(navContentRef.current, { pointerEvents: "auto" });
        },
        onReverseComplete: () => {
          gsap.set(navContentRef.current, { 
            display: "none",
            pointerEvents: "none" 
          });
          setIsAnimating(false);
        }
      });

      // Step 1: Show nav-content and animate background blocks
      tlRef.current.set(navContentRef.current, { display: "block" });
      tlRef.current.to(navBgRef.current, {
        duration: 0.8,
        scaleY: 1,
        stagger: 0.1,
        ease: "power3.inOut",
      });

      // Step 2: Animate nav items clipPath
      tlRef.current.to(navItemsRef.current, {
        duration: 0.7,
        clipPath: "polygon(0 0, 100% 0, 100% 100%, 0% 100%)",
        ease: "power3.inOut",
      }, "-=0.5");
    }
  }, []);

  // Initialize SplitText
  useEffect(() => {
    if (navItemsRef.current) {
      const links = navItemsRef.current.querySelectorAll("a");
      if (links.length) {
        splitLinksRef.current = SplitText.create(links, { 
          type: "lines",
          linesClass: "line",
        });
      }
    }
  }, [isMenuOpen]); // Re-run when menu opens

  const animateLinksIn = () => {
    const linkBlocks = [
      ".nav-socials .line",
      ".nav-primary-links .line",
      ".nav-items-signup .line"
    ];
    
    linkBlocks.forEach((selector) => {
      gsap.fromTo(
        selector,
        { y: "100%", opacity: 0 },
        { 
          y: "0%",
          opacity: 1,
          duration: 0.7,
          ease: "power3.out",
          stagger: 0.05,
          delay: 0.2,
        }
      );  
    });
  };

  const handleCloseMenu = () => {
    if (isAnimating || !isMenuOpen) return;
    setIsAnimating(true);
    
    if (navTogglerRef.current) {
      navTogglerRef.current.classList.remove("open");
    }
    
    if (tlRef.current) {
      tlRef.current.reverse();
    }
    
    setIsMenuOpen(false);
  };

  const handleToggle = () => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (navTogglerRef.current) {
      navTogglerRef.current.classList.toggle("open");
    }

    if (!isMenuOpen && tlRef.current) {
      // Reset before playing
      gsap.set(navBgRef.current, { scaleY: 0 });
      gsap.set(navContentRef.current, { display: "none", pointerEvents: "none" });
      gsap.set(navItemsRef.current, { clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)" });
      
      tlRef.current.play();
      animateLinksIn();
    } else if (tlRef.current) {
      tlRef.current.reverse();
    }

    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavClick = (title, href) => {
    setActive(title);
    handleCloseMenu();
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (tlRef.current) {
        tlRef.current.kill();
      }
      if (splitLinksRef.current) {
        splitLinksRef.current.revert();
      }
    };
  }, []); 

  return (
    <>
      <nav ref={navRef} className="fixed-nav">
        <Link to="/" 
          className={`py-2 ml-4 flex flex-row  items-center justify-center
            ${isScrolled ? ' w-[40px] pl-4' : 'w-[140px] bg-white'}
            transition-all duration-300
          `}
          style={{ transformOrigin: 'left' }}
        >
          <img src={Logo} alt="logo" className="w-[30px] h-[30px]" />
          <img
            src={LogoName1}
            alt="logo"
            className={`w-[80px] h-[30px] object-contain transition-all duration-300 ${
              isScrolled 
                ? 'opacity-0 w-0 scale-x-0' 
                : 'opacity-100 w-[80px] scale-x-100 px-2'
            }`}
            style={{ transformOrigin: 'left' }}
          />
        </Link>
        
        <motion.button 
          initial="initial"
          whileHover="hovered"
          animate={active === "MENU" ? "hovered" : "initial"}
          className={`flex flex-row gap-2 ${styles.flexCenter} hover:bg-white transition-colors duration-300 group`}
          onClick={handleToggle}
        >
          <motion.div 
            className={`relative font-medium ss:text-sm text-ss cursor-pointer overflow-hidden
              ${active === "MENU" ? "text-fourth" : "text-white"}
              ${
                isScrolled 
                  ? 'opacity-0 w-0 scale-x-0' 
                  : 'opacity-100 w-full scale-x-100'
              }
              group-hover:text-fourth transition-colors duration-300
            `}
              style={{ transformOrigin: 'right' }}
          >
            <motion.div 
              variants={{
                initial: { y: 0 },
                hovered: { y: "-100%" },
              }}
              onClick={() => {
                handleCloseMenu();
              }}
              style={{ cursor: 'pointer' }}
            >
              <h2 className="pl-2 text-base text-xs font-bold tracking-tight leading-snug">
                MENU
              </h2>
            </motion.div>
            <motion.div 
              variants={{
                initial: { y: "100%" },
                hovered: { y: 0 },
              }}
              onClick={() => {
                handleCloseMenu();
              }}
              className="absolute inset-0"
              style={{ cursor: 'pointer' }}
            >
              <h2 className="pl-2 text-base text-xs font-bold tracking-tight leading-snug">
                MENU
              </h2>
            </motion.div>
          </motion.div>
          <div 
            className="nav-toggler p-2 group-hover:p-4 group-hover:bg-fourth border-1 border-dashed border-second transition-all duration-300"
            ref={navTogglerRef}
          >
            <span className="group-hover:bg-white transition-colors duration-300"></span>
            <span className="group-hover:bg-white transition-colors duration-300"></span>
          </div>
        </motion.button>

        {/* Fullscreen Menu Overlay */}
        <div className="nav-content drop-shadow-md" ref={navContentRef}>
          {/* Animated Background Blocks */}
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>
          <div className="nav-bg" ref={el => el && navBgRef.current.push(el)}></div>

          {/* Menu Content */}
          <div className="relative z-[2] w-full h-full"
            style={{ 
            backgroundImage: `url(${s10})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            transform: [{ scaleX: -1 }],
            zIndex: 2,
          }}
          >
            <div 
              ref={navItemsRef}
              className="flex flex-col gap-8 p-32 bg-white [clip-path:polygon(0%_0%,100%_0%,100%_0%,0%_0%)] will-change-[clip-path] h-[100svh] px-8 justify-between py-20 sm:flex-row sm:h-auto sm:p-32 sm:justify-between sm:py-32"
            >
              <div>
                {/* Social Media Links */}
                {socialMedia.map((social, index) => (
                  <div className="my-2" key={social.id}>
                    <motion.div 
                      initial="initial"
                      whileHover="hovered" 
                      className={`relative font-medium text-sm cursor-pointer overflow-hidden
                        ${active === social.title ? "text-fourth" : "text-fourth"}
                        hover:text-second transition-colors duration-300
                      `}
                    >
                      <motion.div 
                        variants={{
                          initial: { y: 0 },
                          hovered: { y: "-100%" },
                        }}
                        onClick={() => {
                          window.open(social.link, '_blank');
                          handleCloseMenu();
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {social.title}
                      </motion.div>
                      <motion.div 
                        variants={{
                          initial: { y: "100%" },
                          hovered: { y: 0 },
                        }}
                        onClick={() => {
                          window.open(social.link, '_blank');
                          handleCloseMenu();
                        }}
                        className="absolute inset-0"
                        style={{ cursor: 'pointer' }}
                      >
                        {social.title}
                      </motion.div>
                    </motion.div>
                  </div>
                ))}        

                <div className="mt-6">
                  {isAuthenticated ? (
                    <button 
                      onClick={() => {
                        // Clear user session
                        localStorage.removeItem('isAuthenticated');
                        localStorage.removeItem('user');
                        localStorage.removeItem('isAdmin');
                        localStorage.removeItem('loginTime');
                        handleCloseMenu();
                        // Redirect to home page or login page
                        window.location.href = '/';
                      }}
                      className="text-sm hover:text-second text-fourth font-semibold transition-colors duration-300"
                    >
                      Log Out
                    </button>
                  ) : (
                    <Link 
                      to="/login" 
                      onClick={handleCloseMenu}
                      className="text-sm hover:text-second text-fourth font-semibold transition-colors duration-300"
                    >
                      Log In
                    </Link>
                  )}
                </div>
              </div>

              <div>
                <ul className="list-none">
                  {navLinks.map((nav, index) => (
                    <li 
                      key={nav.id || index}    
                      className="mb-2"                  
                    >
                      <Link 
                        to={nav.href}
                        onClick={() => handleNavClick(nav.title, nav.href)}
                        className={`block text-fourth hover:text-second transition-colors duration-300 cursor-pointer
                          ${active === nav.title 
                            ? "font-extrabold text-md text-second" 
                            : "font-semibold text-md"
                          } 
                        `}
                      >
                        {nav.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-fourth flex flex-col gap-20 sm:justify-between w-full sm:w-[300px] p-4 md:p-6">
                <div className="flex sm:flex-row flex-col items-center gap-6 sm:justify-between">
                  <p className="text-primary font-semibold text-sm">Reservations open now.</p>
                  <p className="text-primary font-semibold text-sm">Schedule a meeting</p>
                </div>
                <div className="bg-primary/80 w-full">
                  <Reveal>
                    <Link to="/contact">
                      <motion.div 
                        initial="initial"
                        whileHover="hovered"
                        className="flex flex-row items-center justify-center gap-2 cursor-pointer group relative overflow-hidden"
                      >
                        {/* Arrow icon container with animation */}
                        <div className="w-[40px] h-[40px] relative mr-2 overflow-hidden">
                          <motion.div 
                            variants={{
                              initial: { y: 0 },
                              hovered: { y: "-100%" },
                            }}
                            className="absolute inset-0 bg-second flex items-center justify-center"
                          >
                            <FiCalendar size={20} className="text-fourth" />
                          </motion.div>
                          <motion.div 
                            variants={{
                              initial: { y: "100%" },
                              hovered: { y: 0 },
                            }}
                            className="absolute inset-0 bg-white flex items-center justify-center"
                          >
                            <FiCalendar size={20} className="text-second" />
                          </motion.div>
                        </div>
                        
                        {/* Text with animation */}
                        <div className="relative overflow-hidden mr-4">
                          <motion.h2 
                            variants={{
                              initial: { y: 0 },
                              hovered: { y: "-100%" },
                            }}
                            className="text-fourth xs:text-sm text-mm font-lato font-semibold"
                          >
                            Book meeting
                          </motion.h2>
                          <motion.h2 
                            variants={{
                              initial: { y: "100%" },
                              hovered: { y: 0 },
                            }}
                            className="absolute inset-0 text-second xs:text-sm text-mm font-lato font-semibold"
                          >
                            Book meeting
                          </motion.h2>
                        </div>
                      </motion.div>
                    </Link>
                  </Reveal>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;